import React, { useState, useEffect } from 'react';
import {
  getDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from '../../services/migration/personnelDataService';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business'; // Şube ikonu, departman için de kullanılabilir veya değiştirilebilir
import CorporateFareIcon from '@mui/icons-material/CorporateFare'; // Departman için daha uygun bir ikon
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// Departman veri yapısı
interface Department {
  id: number | string;
  name: string;
  description?: string;
  head?: string; // Departman sorumlusu (opsiyonel)
  createdAt: string;
  branchesId?: string | null; // Departmanın hangi şubeye ait olduğu (opsiyonel)
}

interface Branch {
  id: string;
  name: string;
}

interface DepartmentModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string; // Eğer modal bir şube context'inden açılıyorsa
  isManager?: boolean; // Kullanıcının yönetici olup olmadığı
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({ 
  open, 
  onClose, 
  branchId, 
  isManager = false 
}) => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  // ✅ AuthContext'ten branches VE userDetails al - Firebase sorgusu YOK!
  const { branches, userDetails } = useAuth();
  
  const [departmentName, setDepartmentName] = useState('');
  const [departmentDescription, setDepartmentDescription] = useState('');
  const [departmentHead, setDepartmentHead] = useState('');
  const [error, setError] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [selectedBranchForDepartment, setSelectedBranchForDepartment] = useState<Branch | null>(null);
  
  // ✅ Şirket ID'sini AuthContext cache'inden al - Firebase sorgusu YOK!
  useEffect(() => {
    const loadCompanyAndData = async () => {
      try {
        setInitialLoading(true);
        
        // ✅ companyId artık AuthContext'ten geliyor
        const currentCompanyId = userDetails?.companyId;
        
        if (!currentCompanyId) {
          setInitialLoading(false);
          return;
        }
        
        setCompanyId(currentCompanyId);
        loadDepartments(currentCompanyId);
        
      } catch (err) {
        console.error('Şirket veya veri yüklenirken hata:', err);
        setInitialLoading(false);
      }
    };
    
    if (open && userDetails?.companyId) {
      loadCompanyAndData();
    } else if (open && !userDetails?.companyId) {
      setInitialLoading(false);
    }
  }, [open, branchId, userDetails?.companyId]);
  
  // Yönetici ise şube seçimini otomatik ayarla
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      const managerBranch = branches.find(b => b.id === branchId);
      if (managerBranch) {
        setSelectedBranchForDepartment(managerBranch);
      }
    }
  }, [isManager, branchId, branches]);
  
  // ✅ Departmanları Cloud Functions üzerinden yükle
  const loadDepartments = async (currentCompanyId: string) => {
    try {
      // Faz 3: departmanlar SQL-once + HybridFallback (personnelDataService).
      const departmentData = await getDepartments(currentCompanyId);
      const departmentsList: Department[] = departmentData.map((d) => ({
        id: d.id,
        name: d.name || '',
        description: (d.description as string) || '',
        head: (d.head as string) || '',
        createdAt: (d.createdAt as string) || '',
        branchesId: (d.branchesId as string | null) || null,
      }));
      
      departmentsList.sort((a, b) => a.name.localeCompare(b.name));
      setDepartments(departmentsList);
    } catch (err) {
      console.error('Departman yüklenirken hata:', err);
      setDepartments([]);
    } finally {
      setInitialLoading(false);
    }
  };
  
  const resetForm = () => {
    setDepartmentName('');
    setDepartmentDescription('');
    setDepartmentHead('');
    setError('');
    if (!(isManager && branchId)) { // Yönetici ve şube ID'si varsa şube seçimini sıfırlama
        setSelectedBranchForDepartment(null);
    }
  };
  
  const handleClose = () => {
    resetForm();
    setSelectedDepartment(null);
    onClose();
  };
  
  const handleSelectDepartment = (dept: Department) => {
    setSelectedDepartment(dept);
    setDepartmentName(dept.name);
    setDepartmentDescription(dept.description || '');
    setDepartmentHead(dept.head || '');
    
    if (dept.branchesId) {
      const deptBranch = branches.find(b => b.id === dept.branchesId);
      if (deptBranch) {
        setSelectedBranchForDepartment(deptBranch);
      } else {
        setSelectedBranchForDepartment(null);
      }
    } else {
      setSelectedBranchForDepartment(null);
    }
  };
  
  const handleDeleteDepartment = async (dept: Department) => {
    if (!companyId) {
      setSnackbarMessage(hr('departmentModal.messages.companyNotFound', 'Company information not found'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setLoading(true);
      // Faz 3: departman silme SQL'e (soft-delete). RTDB'ye yazilmaz.
      await deleteDepartment(dept.id);
      setSnackbarMessage(hr('departmentModal.messages.deleted', 'Department deleted successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      if (selectedDepartment?.id === dept.id) {
        setSelectedDepartment(null);
        resetForm();
      }
      await loadDepartments(companyId);
    } catch (err) {
      console.error('Departman silinirken hata:', err);
      setSnackbarMessage(hr('departmentModal.messages.deleteError', 'An error occurred while deleting department'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddOrUpdateDepartment = async () => {
    if (!departmentName.trim()) {
      setError(hr('departmentModal.validation.nameRequired', 'Department name cannot be empty'));
      return;
    }
    
    if (!companyId) {
      setError(hr('departmentModal.messages.companyNotFound', 'Company information not found'));
      return;
    }
    
    try {
      setLoading(true);
      const input = {
        name: departmentName.trim(),
        branchesId: selectedBranchForDepartment?.id || (isManager && branchId ? branchId : null),
        description: departmentDescription.trim() || null,
        head: departmentHead.trim() || null,
      };
      
      if (selectedDepartment) {
        // Faz 3: departman guncelleme SQL'e. RTDB'ye yazilmaz.
        await updateDepartment(selectedDepartment.id, input);
        setSnackbarMessage(hr('departmentModal.messages.updated', 'Department updated successfully'));
      } else {
        // Faz 3: departman olusturma SQL'e. RTDB'ye yazilmaz.
        await createDepartment(input);
        setSnackbarMessage(hr('departmentModal.messages.added', 'Department added successfully'));
      }
      
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      resetForm();
      setSelectedDepartment(null);
      await loadDepartments(companyId);
    } catch (err) {
      console.error('Departman kaydedilirken hata:', err);
      setError(hr('departmentModal.messages.saveError', 'An error occurred while saving department'));
      setSnackbarMessage(hr('departmentModal.messages.saveError', 'An error occurred while saving department'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getBranchNameForDisplay = (deptBranchId: string | null | undefined): string => {
    if (!deptBranchId) return hr('departmentModal.general', 'General');
    const branch = branches.find(b => b.id === deptBranchId);
    return branch ? branch.name : hr('departmentModal.unknownBranch', 'Unknown Branch');
  };
  
  const getButtonText = () => {
    if (loading) return hr('departmentModal.saving', 'Saving...');
    return selectedDepartment ? hr('departmentModal.updateDepartment', 'Update Department') : hr('departmentModal.addDepartment', 'Add New Department');
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{hr('departmentModal.title', 'Company Departments')}</Typography>
          <IconButton edge="end" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {selectedDepartment ? hr('departmentModal.editDepartment', 'Edit Department') : hr('departmentModal.addDepartment', 'Add New Department')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Autocomplete
                  fullWidth
                  value={selectedBranchForDepartment}
                  onChange={(event, newValue) => {
                    if (!(isManager && branchId)) { // Sadece yönetici değilse veya şube ID'si yoksa değiştirebilsin
                        setSelectedBranchForDepartment(newValue);
                    }
                  }}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={hr('departmentModal.fields.departmentBranchOptional', 'Department Branch (Optional)')}
                      size="small"
                      placeholder={hr('departmentModal.fields.selectBranch', 'Select branch...')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      disabled={loading || (isManager && !!branchId)} // Yönetici ve şube ID'si varsa disable et
                      sx={{ mb: 2 }}
                    />
                  )}
                  disabled={loading || (isManager && !!branchId)}
                />
                
                <TextField
                  autoFocus
                  margin="dense"
                  id="departmentName"
                  label={hr('departmentModal.fields.departmentName', 'Department Name')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={departmentName}
                  onChange={(e) => {
                    setDepartmentName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                
                <TextField
                  margin="dense"
                  id="departmentDescription"
                  label={hr('departmentModal.fields.descriptionOptional', 'Description (Optional)')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={departmentDescription}
                  onChange={(e) => setDepartmentDescription(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                  multiline
                  rows={2}
                />

                <TextField
                  margin="dense"
                  id="departmentHead"
                  label={hr('departmentModal.fields.departmentHeadOptional', 'Department Head (Optional)')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={departmentHead}
                  onChange={(e) => setDepartmentHead(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  {selectedDepartment && (
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setSelectedDepartment(null);
                        resetForm();
                      }}
                      disabled={loading}
                    >
                      {hr('common.cancel', 'Cancel')}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAddOrUpdateDepartment}
                    disabled={!departmentName.trim() || loading}
                    fullWidth={!selectedDepartment}
                    sx={{ ml: selectedDepartment ? 2 : 0 }}
                    startIcon={loading ? <CircularProgress size={18} /> : <AddIcon />}
                  >
                    {getButtonText()}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {hr('departmentModal.existingDepartments', 'Existing Departments')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {departments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {hr('departmentModal.noDepartments', 'No departments have been defined yet.')}
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {isEnglish ? `Showing ${departments.length} departments.` : `Toplam ${departments.length} departman listeleniyor.`}
                    </Typography>
                    <Table size="small" sx={{ minWidth: 400 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{hr('departmentModal.columns.departmentName', 'Department Name')}</TableCell>
                          <TableCell>{hr('departmentModal.columns.branch', 'Branch')}</TableCell>
                          <TableCell>{hr('departmentModal.columns.head', 'Head')}</TableCell>
                          <TableCell align="right">{hr('departmentModal.columns.actions', 'Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {departments.map((dept) => (
                          <TableRow 
                            key={dept.id} 
                            hover
                            sx={{ 
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              ...(selectedDepartment?.id === dept.id ? 
                                { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : {}),
                              cursor: 'pointer'
                            }}
                            onClick={() => handleSelectDepartment(dept)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CorporateFareIcon sx={{ fontSize: '1.1rem', mr: 1, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontWeight: selectedDepartment?.id === dept.id ? 'bold' : 'normal' }}>
                                  {dept.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {getBranchNameForDisplay(dept.branchesId)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {dept.head || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDepartment(dept);
                                }}
                                sx={{ color: 'error.main' }}
                                disabled={loading}
                                title={hr('common.delete', 'Delete')}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>{hr('common.close', 'Close')}</Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DepartmentModal; 